import _upperFirst from 'lodash.upperfirst'
import _cloneDeep from 'lodash.clonedeep'
import _isEqual from 'lodash.isequal'
import moment from 'moment-timezone'
import { formatCurrency, formatDate, formatTime, convertToPassengerCredits, describeStoredExtraRoleChanges, getRoleNames, hasStoredAgentStaffRole, getArchiveConfirmationCode } from '@smarttransit/common'
import { extractBaseNameFromFilePath } from '@smarttransit/common-client'
import { addAlert, isAuthorized } from '../../../../utilities/helpers'
import AgentPhotoIdCard from '../components/agent-photo-id-card/template.vue'
import agentPhotoId from '../components/agent-photo-id'
import photoIdFiles from '../components/agent-photo-id-files'

import {
  archiveAgent,
  getAgent,
  getAgentSecureUrl,
  getAgentPhotoSecureUrl,
  getAgentPhotoFile,
  getAgentTemplate,
  registerAgent,
  registerAgentWithExistingUser,
  resendAgentInvite,
  updateAgentExtraRoles,
  unArchiveAgent,
  updateAgent
} from '../../../../services/agents-service'

import { getCachedAgentConversionRatePercent } from '../../../../services/st-settings-service'

const { canIssueAgentPhotoIdCard, resolveAgentPhotoIdRoleLabel, formatAgentPhotoIdNumber } = agentPhotoId

let _cachedComponentData = null

export default {
  name: 'agents-agent',
  components: {
    AgentPhotoIdCard
  },
  props: {
    signedInUser: Object
  },
  data () {
    return {
      apiInProgress: false,
      toggleInProgress: false,
      currentAgent: null,
      currentAgentOriginal: null,
      currentAgentError: null,
      currentCountryCurrency: null,
      defaultAgentConversionRate: '100',
      localTimezone: moment.tz.guess(),
      timezones: [],
      frontIdDocuments: { documentsToUpload: [], uploadedDocuments: [] },
      backIdDocuments: { documentsToUpload: [], uploadedDocuments: [] },
      avatarDocuments: { documentsToUpload: [], uploadedDocuments: [] },
      avatarPreviewBeforeDelete: null,
      profilePhotoMimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
      isEditable: false,
      isAdmin: false,
      selectedUser: null,
      profitCalculatorOpen: false,
      totalsDialogOpen: false,
      extraRolesDialogOpen: false,
      extraRolesIndependent: {
        asAgentStaff: false,
        asAgentDriver: false,
        asTerminalSupervisor: false
      },
      extraRolesOpenedWith: {
        asAgentStaff: false,
        asAgentDriver: false,
        asTerminalSupervisor: false
      },
      creditRefundsExpiryDialogOpen: false,
      creditRefundsExpiryDraft: null,
      creditRefundsExpiryDisabled: false,
      savingCreditRefundsExpiry: false,
      photoIdDialogOpen: false,
      photoIdLoading: false,
      photoIdImageSrc: '',
      photoIdPreviewUrl: '',
      photoIdCanvas: null,
      photoIdFileStamp: null
    }
  },
  computed: {
    computedDocumentExpiryDateMin () {
      return moment(new Date()).add(2, 'weeks').format('Y-MM-DD')
    },
    computedCurrency () {
      return (this.currentCountryCurrency && this.currentCountryCurrency.currencySymbol) || ''
    },
    currentBalanceLabel () {
      if (!this.currentAgent || this.currentAgent.balance == null) {
        return '—'
      }
      return formatCurrency(this.currentAgent.balance, this.computedCurrency)
    },
    agentNameLabel () {
      if (!this.currentAgent) {
        return ''
      }
      return [this.currentAgent.firstName, this.currentAgent.lastName].filter(Boolean).join(' ')
    },
    isSuperAdmin () {
      return isAuthorized(this.$props.signedInUser, 'superadmin')
    },
    computedPassengerCredits () {
      const conversionRate = this.currentAgent?.customConversionRatePercent || this.defaultAgentConversionRate

      if (conversionRate) {
        return formatCurrency(convertToPassengerCredits({ amountAsCents: 100, conversionRatePercent: Number(conversionRate) }))
      } else {
        return ''
      }
    },
    computedProfit () {
      if (this.computedPassengerCredits) {
        let profit = Math.floor(Number(this.computedPassengerCredits) * 100) - 100
        return formatCurrency(profit)
      }
    },
    isDataEdited () {
      return !_isEqual(this.currentAgent, this.currentAgentOriginal)
    },
    extraRoleStaffChecked: {
      get () {
        return !!(this.extraRolesIndependent.asAgentStaff || this.extraRolesIndependent.asTerminalSupervisor)
      },
      set (value) {
        if (this.extraRolesIndependent.asTerminalSupervisor) {
          return
        }
        this.extraRolesIndependent = { ...this.extraRolesIndependent, asAgentStaff: value }
      }
    },
    extraRoleDriverChecked: {
      get () {
        return !!(this.extraRolesIndependent.asAgentDriver || this.extraRolesIndependent.asTerminalSupervisor)
      },
      set (value) {
        if (this.extraRolesIndependent.asTerminalSupervisor) {
          return
        }
        this.extraRolesIndependent = { ...this.extraRolesIndependent, asAgentDriver: value }
      }
    },
    isStoredAgentStaff () {
      return hasStoredAgentStaffRole(getRoleNames(this.currentAgent && this.currentAgent.stUser))
    },
    creditRefundsExpiryLabel () {
      if (!this.currentAgent || !this.currentAgent.creditRefundsExpiry) {
        return ''
      }
      const expiry = new Date(this.currentAgent.creditRefundsExpiry)
      if (!expiry.getTime() || expiry.getTime() <= Date.now()) {
        return ''
      }
      return `${formatTime(this.currentAgent.creditRefundsExpiry)}, ${formatDate(this.currentAgent.creditRefundsExpiry)}`
    },
    computedCreditRefundsExpiryMin () {
      return moment().add(1, 'day').format('Y-MM-DD')
    },
    agentRoleNames () {
      return getRoleNames(this.currentAgent && this.currentAgent.stUser)
    },
    hasStoredProfilePhoto () {
      return !!(this.currentAgent && this.currentAgent.stUser && this.currentAgent.stUser.avatarSrc)
    },
    canOpenPhotoId () {
      return !!(
        this.currentAgent &&
        this.currentAgent.id &&
        this.hasStoredProfilePhoto &&
        !this.currentAgent.deleteAvatar &&
        canIssueAgentPhotoIdCard(this.agentRoleNames)
      )
    },
    photoIdRoleLabel () {
      return resolveAgentPhotoIdRoleLabel(this.agentRoleNames) || ''
    },
    photoIdNumber () {
      if (!this.currentAgent) {
        return ''
      }
      return formatAgentPhotoIdNumber(this.currentAgent.id, this.currentAgent.countryId)
    },
    photoIdPngFileName () {
      return photoIdFiles.buildAgentPhotoIdFileName(this.agentNameLabel, 'png', this.photoIdFileStamp)
    },
    photoIdPdfFileName () {
      return photoIdFiles.buildAgentPhotoIdFileName(this.agentNameLabel, 'pdf', this.photoIdFileStamp)
    },
    photoIdPreviewBoxStyle () {
      return {
        width: photoIdFiles.CR80_WIDTH_MM + 'mm',
        height: photoIdFiles.CR80_HEIGHT_MM + 'mm'
      }
    }
  },
  watch: {
    selectedUser (newVal) {
      if (newVal) {
        this.currentAgent = { ...this.currentAgent, stUserId: newVal.id }
        // this.currentAgent.stUserId = newVal.id
      } else if (this.currentAgent.stUserId) {
        delete this.currentAgent.stUserId
      }
    },
    frontIdDocuments (documents) {
      this.currentAgent.idDocumentFrontFile = null

      if (documents.documentsToUpload) {
        documents.documentsToUpload.forEach((newFile) => {
          if (!newFile.error) {
            this.currentAgent = {
              ...this.currentAgent,
              idDocumentFrontFile: newFile.file,
              idDocumentFrontFilename: newFile.name
            }
          }
        })
      }
    },
    backIdDocuments (documents) {
      this.currentAgent.idDocumentBackFile = null

      if (documents.documentsToUpload) {
        documents.documentsToUpload.forEach((newFile) => {
          if (!newFile.error) {
            this.currentAgent = {
              ...this.currentAgent,
              idDocumentBackFile: newFile.file,
              idDocumentBackFilename: newFile.name
            }
          }
        })
      }
    },
    avatarDocuments (documents) {
      if (!this.currentAgent) {
        return
      }
      const next = photoIdFiles.nextAvatarFormState(this.currentAgent, documents)
      if (!next) {
        return
      }
      this.currentAgent = {
        ...this.currentAgent,
        avatarFile: next.avatarFile,
        deleteAvatar: next.deleteAvatar
      }
    }
  },
  mounted () {
    async function onRouterLoad () {
      if (this.$route.params.agentId + '' === '0') {
        if (this.isAdmin) {
          this.currentAgent = getAgentTemplate()
          this.currentAgent.stUser.timezone = this.localTimezone
          this.setCurrentAgent()
          this.setDefaultAgentConversionRate()
        } else {
          this.currentAgentError = 'The current user does not have permission to create an agent'
        }
      } else {
        this.loadAgent(this.$route.params.agentId)
      }
    }

    this.isAdmin = isAuthorized(this.$props.signedInUser, 'admin')
    this.isEditable = this.isAdmin
    this.$nextTick(onRouterLoad.bind(this))
  },
  methods: {
    validForm () {
      return this.$refs &&
        this.$refs.form &&
        (this.signedInUser || this.$refs.form.validate()) &&
        (this.signedInUser || this.currentAgent.email || this.currentAgent.phoneNumber)
    },
    clearFrontIdUpload () {
      this.currentAgent.idDocumentFrontFile = null

      this.frontIdDocuments = {
        ...this.frontIdDocuments,
        documentsToUpload: []
      }
    },
    clearBackIdUpload () {
      this.currentAgent.idDocumentBackFile = null

      this.backIdDocuments = {
        ...this.backIdDocuments,
        documentsToUpload: []
      }
    },
    clearAvatarUpload () {
      if (!this.currentAgent) {
        return
      }
      this.avatarPreviewBeforeDelete = null
      this.currentAgent.avatarFile = null
      this.currentAgent.deleteAvatar = false
      this.avatarDocuments = {
        documentsToUpload: [],
        uploadedDocuments: []
      }
    },
    markAvatarForDelete () {
      const documents = this.avatarDocuments || {}
      this.avatarPreviewBeforeDelete = {
        documentsToUpload: (documents.documentsToUpload || []).slice(),
        uploadedDocuments: (documents.uploadedDocuments || []).slice()
      }
      this.avatarDocuments = {
        documentsToUpload: [],
        uploadedDocuments: []
      }
      this.currentAgent = {
        ...this.currentAgent,
        avatarFile: null,
        deleteAvatar: true
      }
    },
    cancelAvatarDelete () {
      const preview = this.avatarPreviewBeforeDelete
      this.avatarPreviewBeforeDelete = null
      this.avatarDocuments = preview || { documentsToUpload: [], uploadedDocuments: [] }
      this.currentAgent = {
        ...this.currentAgent,
        avatarFile: null,
        deleteAvatar: false
      }
    },
    async loadAvatarPreview () {
      this.avatarPreviewBeforeDelete = null
      this.avatarDocuments = { documentsToUpload: [], uploadedDocuments: [] }
      this.currentAgent.deleteAvatar = false
      this.currentAgent.avatarFile = null
      const stUser = this.currentAgent.stUser
      if (!this.currentAgent.id || !stUser || !stUser.avatarSrc) {
        return
      }
      try {
        const secureUrl = await getAgentPhotoSecureUrl({ id: this.currentAgent.id })
        const metadata = stUser.metadata || {}
        this.avatarDocuments = {
          documentsToUpload: [],
          uploadedDocuments: [{
            filename: extractBaseNameFromFilePath(stUser.avatarSrc),
            type: metadata.mimetype || 'image/jpeg',
            size: metadata.size,
            url: secureUrl
          }]
        }
      } catch (error) {
        addAlert({ message: error, type: 'error' })
      }
    },
    async openPhotoIdDialog () {
      if (!this.canOpenPhotoId) {
        return
      }
      this.photoIdDialogOpen = true
      this.photoIdLoading = true
      this.photoIdPreviewUrl = ''
      this.photoIdCanvas = null
      this.photoIdFileStamp = new Date()
      try {
        const file = await getAgentPhotoFile({ id: this.currentAgent.id })
        this.photoIdImageSrc = 'data:' + (file.contentType || 'image/jpeg') + ';base64,' + file.data
        await this.$nextTick()
        const canvas = await this.$refs.photoIdCard.capture()
        this.photoIdCanvas = canvas
        this.photoIdPreviewUrl = canvas.toDataURL('image/png')
      } catch (error) {
        this.photoIdDialogOpen = false
        addAlert({ message: error, type: 'error' })
      } finally {
        this.photoIdLoading = false
      }
    },
    async downloadPhotoIdPng () {
      return this.photoIdCanvas.toDataURL('image/png')
    },
    async downloadPhotoIdPdf () {
      return photoIdFiles.pdfBytesFromCanvas(this.photoIdCanvas)
    },
    onChangeCountryId (val) {
      this.currentAgent.countryId = val
      this.setDefaultAgentConversionRate()
    },
    setDefaultAgentConversionRate () {
      getCachedAgentConversionRatePercent({ countryId: this.currentAgent.countryId })
        .then((result) => {
          this.defaultAgentConversionRate = result?.conversionRatePercent
        }).catch((err) => {
          addAlert({ message: err, type: 'error' })
        })
    },
    refreshView () {
      this.$router.go()
    },
    async loadAgent (id) {
      try {
        this.currentAgent = await getAgent({ id, includeArchived: true })
        await this.setCurrentAgent()
        this.setDefaultAgentConversionRate()
      } catch (error) {
        addAlert({ message: error, type: 'error' })
      }
    },
    save () {
      if (!this.signedInUser && !this.currentAgent.phoneNumber) {
        return alert('Please provide a mobile number')
      }

      if (this.validForm()) {
        if (this.currentAgent.id) {
          this.updateAgent()
        } else {
          if (!this.currentAgent.idDocumentFrontFile || !this.currentAgent.idDocumentBackFile) {
            return alert('Please provide ID Documents (front and back)')
          }

          this.createAgent()
        }
      }
    },
    async updateAgent () {
      try {
        this.apiInProgress = true
        await updateAgent({ ...this.currentAgent })
        this.clearFrontIdUpload()
        this.clearBackIdUpload()
        this.clearAvatarUpload()
        await this.loadAgent(this.currentAgent.id)
        addAlert({ message: 'Successfully updated agent', type: 'success' })
      } catch (error) {
        addAlert({ message: error, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    },
    async createAgent () {
      try {
        this.apiInProgress = true
        let currentAgent

        if (this.currentAgent.stUserId) {
          currentAgent = await registerAgentWithExistingUser({ ...this.currentAgent })
        } else {
          currentAgent = await registerAgent({ ...this.currentAgent })
        }

        this.clearFrontIdUpload()
        this.clearBackIdUpload()
        this.clearAvatarUpload()
        await this.loadAgent(currentAgent.id)
        addAlert({ message: 'Successfully registered agent', type: 'success' })
        this.$router.push({ name: 'agents' })
      } catch (error) {
        addAlert({ message: error, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    },
    async onArchive () {
      if (this.isAdmin) {
        const code = getArchiveConfirmationCode(this.currentAgent.phoneNumber, this.currentAgent.id)
        const fromIdHint = this.currentAgent.phoneNumber ? '' : ' (from the agent ID)'
        const promptResult = prompt(`Enter "${code}" below to confirm ARCHIVING this agent${fromIdHint}`)

        if (promptResult) {
          if (promptResult !== code) {
            alert('Please check the code entered and try again')
          } else {
            try {
              this.apiInProgress = true
              await archiveAgent(this.currentAgent.id)
              await this.loadAgent(this.currentAgent.id)

              addAlert({
                message: `Successfully archived agent ${this.currentAgent.firstName} ${this.currentAgent.lastName}`,
                type: 'success'
              })
            } catch (err) {
              addAlert({ message: err, type: 'error' })
            } finally {
              this.apiInProgress = false
            }
          }
        }
      }
    },
    async onUndoArchive () {
      if (this.isAdmin) {
        const code = getArchiveConfirmationCode(this.currentAgent.phoneNumber, this.currentAgent.id)
        const fromIdHint = this.currentAgent.phoneNumber ? '' : ' (from the agent ID)'
        const promptResult = prompt(`Enter "${code}" below to confirm un-archiving this agent${fromIdHint}`)

        if (promptResult) {
          if (promptResult !== code) {
            alert('Please check the code entered and try again')
          } else {
            try {
              this.apiInProgress = true
              await unArchiveAgent(this.currentAgent.id)
              await this.loadAgent(this.currentAgent.id)

              addAlert({
                message: `Successfully un-archived agent ${this.currentAgent.firstName} ${this.currentAgent.lastName}`,
                type: 'success'
              })
            } catch (error) {
              addAlert({ message: error, type: 'error', isModal: true })
            } finally {
              this.apiInProgress = false
            }
          }
        }
      }
    },
    async onResendInvitation () {
      if (this.isEditable && confirm(`Confirm re-sending invitation for ${this.currentAgent.stUser.firstName} ${this.currentAgent.stUser.lastName}`)) {
        try {
          this.apiInProgress = true
          await resendAgentInvite(this.currentAgent.id)
          await this.loadAgent(this.currentAgent.id)
          addAlert({ message: `Successfully re-sent invitation for ${this.currentAgent.stUser.firstName} ${this.currentAgent.stUser.lastName}`, type: 'success' })
        } catch (err) {
          addAlert({ message: err, type: 'error', isModal: true })
        } finally {
          this.apiInProgress = false
        }
      }
    },
    openExtraRolesDialog () {
      this.extraRolesIndependent = {
        asAgentStaff: !!(this.currentAgent && this.currentAgent.isAgentStaff),
        asAgentDriver: !!(this.currentAgent && this.currentAgent.isAgentDriver),
        asTerminalSupervisor: !!(this.currentAgent && this.currentAgent.isTerminalSupervisor)
      }
      this.extraRolesOpenedWith = { ...this.extraRolesIndependent }
      this.extraRolesDialogOpen = true
    },
    closeExtraRolesDialog () {
      this.extraRolesDialogOpen = false
    },
    async saveExtraRoles () {
      const changes = describeStoredExtraRoleChanges(this.extraRolesOpenedWith, this.extraRolesIndependent)
      if (!changes.length) {
        this.extraRolesDialogOpen = false
        return
      }
      if (!confirm('Confirm extra role changes: ' + changes.join(', ') + '?')) {
        return
      }
      try {
        this.toggleInProgress = true
        await updateAgentExtraRoles({
          id: this.currentAgent.id,
          asAgentStaff: this.extraRolesIndependent.asAgentStaff,
          asAgentDriver: this.extraRolesIndependent.asAgentDriver,
          asTerminalSupervisor: this.extraRolesIndependent.asTerminalSupervisor
        })
        this.extraRolesDialogOpen = false
        await this.loadAgent(this.currentAgent.id)
      } catch (err) {
        addAlert({ message: err, type: 'error', isModal: true })
      } finally {
        this.toggleInProgress = false
      }
    },
    loadCreditRefundsExpiryDialog () {
      this.creditRefundsExpiryDialogOpen = true
      this.creditRefundsExpiryDraft = this.currentAgent.creditRefundsExpiry
        ? moment(this.currentAgent.creditRefundsExpiry).format('Y-MM-DD')
        : null
      this.creditRefundsExpiryDisabled = false
    },
    closeCreditRefundsExpiryDialog () {
      this.creditRefundsExpiryDialogOpen = false
      this.creditRefundsExpiryDraft = this.creditRefundsExpiryDisabled ? null : this.creditRefundsExpiryDraft
    },
    async saveCreditRefundsExpiry () {
      if (!this.creditRefundsExpiryDisabled && !this.creditRefundsExpiryDraft) {
        return alert('Please select a date or restrict refunds to the original top-up phone')
      }
      if (!confirm(this.creditRefundsExpiryDisabled || !this.creditRefundsExpiryDraft
        ? 'Confirm restricting refunds to the original top-up phone (same-phone refunds stay allowed)'
        : 'Confirm saving "permitted until" date for different-phone refunds')) {
        return
      }
      try {
        this.savingCreditRefundsExpiry = true
        let parsedExpiryDate = ''
        if (!this.creditRefundsExpiryDisabled && this.creditRefundsExpiryDraft) {
          const parsed = moment(this.creditRefundsExpiryDraft, '')
          parsed.endOf('day')
          parsedExpiryDate = parsed.toISOString()
        }
        await updateAgent({ ...this.currentAgent, creditRefundsExpiry: parsedExpiryDate })
        this.creditRefundsExpiryDialogOpen = false
        await this.loadAgent(this.currentAgent.id)
        addAlert({ message: 'Successfully updated agent credit refund permissions', type: 'success' })
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.savingCreditRefundsExpiry = false
      }
    },
    async setCurrentAgent () {
      if (_cachedComponentData) {
        this.currentAgent = _cachedComponentData
      } else {
        if (this.currentAgent.countryCurrency) {
          this.currentCountryCurrency = this.currentAgent.countryCurrency
        } else {
          const currencyResults = await this.$store.getters.getCountryCurrencies
          this.currentCountryCurrency = currencyResults[this.currentAgent.countryId]
        }

        this.currentAgent.firstName = this.currentAgent.stUser.firstName
        this.currentAgent.lastName = this.currentAgent.stUser.lastName
        this.currentAgent.email = this.currentAgent.stUser.email
        this.currentAgent.phoneNumber = this.currentAgent.stUser.phone
        this.currentAgent.balanceLabel = this.currentBalanceLabel
        this.currentAgent.isExpired = new Date(this.currentAgent.stUser.verificationTokenExpiry).getTime() <= Date.now()
        this.currentAgent.verificationTokenExpiryLabel = this.currentAgent.stUser.verificationToken && this.currentAgent.stUser.verificationType && this.currentAgent.stUser.verificationTokenExpiry ? `${formatTime(this.currentAgent.stUser.verificationTokenExpiry)}, ${formatDate(this.currentAgent.stUser.verificationTokenExpiry)}` : null
        this.currentAgent.dateArchivedLabel = this.currentAgent.dateArchived ? `${formatTime(this.currentAgent.dateArchived)}, ${formatDate(this.currentAgent.dateArchived)}` : null
        this.currentAgent.dateCreatedLabel = this.currentAgent.dateCreated ? `${formatTime(this.currentAgent.dateCreated)}, ${formatDate(this.currentAgent.dateCreated)}` : ''
        this.currentAgent.dateUpdatedLabel = this.currentAgent.dateUpdated ? `${formatTime(this.currentAgent.dateUpdated)}, ${formatDate(this.currentAgent.dateUpdated)}` : ''
        this.currentAgent.userType = this.currentAgent.stUser.roles && this.currentAgent.stUser.roles.length ? this.currentAgent.stUser.roles[0].name : null
        this.currentAgent.userTypeLabel = this.currentAgent.stUser.roles
          ? this.currentAgent.stUser.roles.map((o) => {
            if (o.name === 'investor') {
              return 'Stakeholder'
            }
            if (o.name === 'agentdriver') {
              return 'Agent Driver'
            }
            if (o.name === 'agentstaff') {
              return 'Agent Staff'
            }
            if (o.name === 'terminalsupervisor') {
              return 'Terminal Supervisor'
            }
            return _upperFirst(o.name)
          }).join(', ')
          : ''
        this.currentAgent.isAgentStaff = this.currentAgent.stUser.roles && this.currentAgent.stUser.roles.some(o => o.name === 'agentstaff')
        this.currentAgent.isAgentDriver = this.currentAgent.stUser.roles && this.currentAgent.stUser.roles.some(o => o.name === 'agentdriver')
        this.currentAgent.isTerminalSupervisor = this.currentAgent.stUser.roles && this.currentAgent.stUser.roles.some(o => o.name === 'terminalsupervisor')
        this.currentAgent.requiresAgentCredit = !!this.currentAgent.requiresAgentCredit
        this.currentAgent.timezone = this.currentAgent.stUser.timezone || ''
        this.currentAgent.phoneIsDeleted = this.currentAgent.phoneNumber && this.currentAgent.phoneNumber.indexOf('deleted') > -1

        if (this.currentAgent.agentProfileDocuments && this.currentAgent.agentProfileDocuments.length) {
          const promises = []
          const idDocumentFront = this.currentAgent.agentProfileDocuments.find((o) => (o.metadata.type === 'idDocumentFront'))
          const idDocumentBack = this.currentAgent.agentProfileDocuments.find((o) => (o.metadata.type === 'idDocumentBack'))

          if (idDocumentFront) {
            this.frontIdDocuments = {
              ...this.frontIdDocuments,
              uploadedDocuments: [
                { filename: extractBaseNameFromFilePath(idDocumentFront.documentSrc), type: idDocumentFront.metadata.mimetype, size: idDocumentFront.metadata.size }
              ]
            }

            promises.push(getAgentSecureUrl({ documentId: idDocumentFront.id }))
          }

          if (idDocumentBack) {
            this.backIdDocuments = {
              ...this.backIdDocuments,
              uploadedDocuments: [
                { filename: extractBaseNameFromFilePath(idDocumentBack.documentSrc), type: idDocumentBack.metadata.mimetype, size: idDocumentBack.metadata.size }
              ]
            }

            promises.push(getAgentSecureUrl({ documentId: idDocumentBack.id }))
          }

          let secureUrlResults

          if (promises.length) {
            secureUrlResults = await Promise.all(promises)
          }

          if (idDocumentFront) {
            this.frontIdDocuments.uploadedDocuments[0].url = secureUrlResults[0]

            this.frontIdDocuments = {
              ...this.frontIdDocuments,
              uploadedDocuments: [...this.frontIdDocuments.uploadedDocuments]
            }

            this.currentAgent.idDocumentExpiryDate = idDocumentFront.dateExpires
          }

          if (idDocumentBack) {
            this.backIdDocuments.uploadedDocuments[0].url = secureUrlResults[1]

            this.backIdDocuments = {
              ...this.backIdDocuments,
              uploadedDocuments: [...this.backIdDocuments.uploadedDocuments]
            }
          }
        }

        this.currentAgent.recoveryContact = (this.currentAgent.stUser && this.currentAgent.stUser.metadata && this.currentAgent.stUser.metadata.recoveryContact) || ''
        await this.loadAvatarPreview()
        // this.currentAgent.totalRegisteredPassengers = (this.currentAgent.agentRegisteredPassengers && this.currentAgent.agentRegisteredPassengers.length) || '0'
        this.isEditable = !this.currentAgent.dateArchivedLabel && (this.$props.signedInUser.id === this.currentAgent.stUser.id || this.isAdmin)
      }

      this.currentAgent = { ...this.currentAgent }
      this.currentAgentOriginal = _cloneDeep(this.currentAgent)
    }
  },
  beforeRouteLeave (to, from, next) {
    if (!this.isDataEdited || confirm('Discard changes made?')) {
      _cachedComponentData = null
      next()
    } else {
      _cachedComponentData = this.currentAgent
      next(false)
    }
  }
}
